import React, { useEffect, useState, useRef } from "react";
import "./viewClientInvoiceModal.css";
import { useMutation } from "@apollo/client";
import { CREATE_PENDING_JOB_FORM } from "../../../../utils/GraphQL/mutations";
import BdrModal from "../../../../components/BdrModal/BdrModal";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import ClientModalInvoiceQuotePage from "../ClientModalInvoiceQuotePage/ClientModalInvoiceQuotePage";
import CycleConfirmationModal from "../CycleConfirmationModal/CycleConfirmationModal";
import invoiceHTMLToPDFBase64 from "../../../../utils/invoiceHTMLToPDFBase64";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";

const ViewClientInvoiceModal = ({
  selectedInvoice,
  closeInvoiceModal,
  queryClient,
  emailObjHandler,
  changeParentForEmailModal,
}) => {
  const { handleSnack } = useSnackbar();
  const [createPendingJobFormCall] = useMutation(CREATE_PENDING_JOB_FORM);
  const [staticPrint, setStaticPrint] = useState(false);
  const [displayInvoicePdf, setDisplayInvoicePdf] = useState(false);
  const [cycleModalOpen, setCycleModalOpen] = useState(false);
  const timeoutIdRef = useRef();

  useEffect(() => {
    const timeoutId = timeoutIdRef.current;
    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (displayInvoicePdf === true) {
      preparePDF("invoice-to-print");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayInvoicePdf]);

  useEffect(() => {
    if (staticPrint === true) {
      sendInvoiceEmail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staticPrint]);

  /*
   * No body.style.overflow here. This used to force it back to "auto" on
   * close, which unlocked the PAGE while the day/week modal that opened this
   * was still up. BdrModal keeps a counted lock across stacked modals, so the
   * page stays locked until the last one closes.
   */
  const closeModal = () => {
    closeInvoiceModal("");
  };

  const sendInvoiceEmail = async () => {
    if (selectedInvoice?.invoicePaid === true) {
      emailObjHandler({
        type: "receiptTemplate",
        invoiceLink: `https://bigginsdoorrefinishing.com/client/view-invoice/${selectedInvoice?.id}`,
        attachArr: [],
      });
    }

    if (selectedInvoice?.invoicePaid !== true) {
      emailObjHandler({
        type: "invoiceTemplate",
        invoiceLink: `https://bigginsdoorrefinishing.com/client/view-invoice/${selectedInvoice?.id}`,
        attachArr: [],
      });
    }

    const id = setTimeout(() => {
      changeParentForEmailModal(true);
    }, 700);

    timeoutIdRef.current = id;
    closeModal();
    setStaticPrint(false);
    return;
  };

  const preparePDF = async (elId) => {
    const pdf = await invoiceHTMLToPDFBase64("BDR Invoice", elId, true);

    if (pdf) {
      const id = setTimeout(() => {
        setDisplayInvoicePdf(false);
      }, 2000);
      return () => clearTimeout(id);
    }

    handleSnack("Error preparing print...", 2000);
    setDisplayInvoicePdf(false);
    return;
  };

  const sendToJobReview = async () => {
    try {
      const { data } = await createPendingJobFormCall({
        variables: {
          invoiceId: selectedInvoice?.id,
          clientId: selectedInvoice?.customerId || null,
        },
      });

      if (data?.createPendingJobForm?._id) {
        handleSnack("Sent to Job Review", 3000);
        if (queryClient) queryClient();
      }
    } catch (err) {
      const msg = err?.message || "Something went wrong";
      handleSnack(msg, 4000);
    }
  };

  return (
    <>
      <BdrModal
        open
        onClose={closeModal}
        size="xl"
        /* Opened FROM the day/week modals as often as from a client page, so it
           sits a rung up. Harmless when there is nothing underneath. */
        layer="raised"
        title={
          selectedInvoice?.invoiceNum
            ? `Invoice #${selectedInvoice.invoiceNum}`
            : "Invoice"
        }
        panelClassName="view-invoice-panel"
        actions={
          <>
            <BdrButton onClick={closeModal}>Close</BdrButton>
            {selectedInvoice?.relatedTaskId && (
              <BdrButton variant="action" onClick={() => setCycleModalOpen(true)}>
                Cycle Position
              </BdrButton>
            )}
            {!selectedInvoice?.invoicePaid && (
              <BdrButton variant="confirm" onClick={sendToJobReview}>
                Send to Job Review
              </BdrButton>
            )}
            <BdrButton
              variant="confirm"
              disabled={displayInvoicePdf}
              onClick={() => setDisplayInvoicePdf(true)}
            >
              Print Invoice
            </BdrButton>
            <BdrButton variant="confirm" onClick={() => setStaticPrint(true)}>
              Send Invoice
            </BdrButton>
          </>
        }
      >
        <div className="view-invoice-page-modal-container">
          <ClientModalInvoiceQuotePage
            displayPdf={displayInvoicePdf}
            invoiceObj={selectedInvoice}
          />
        </div>
      </BdrModal>
      {/* Outside the modal — see ViewClientQuoteModal note on portal React-tree
          bubbling. */}
      <CycleConfirmationModal
        open={cycleModalOpen}
        taskId={selectedInvoice?.relatedTaskId || null}
        onClose={() => setCycleModalOpen(false)}
      />
    </>
  );
};

export default ViewClientInvoiceModal;
